import { LifeBuoy, RefreshCw } from "lucide-react";
import { Button } from "../ui";
import { Assistant, Flow, Quick, QuickReplies, UserBubble } from "./FlowParts";

const labelOf = (item, lang) => (lang === "ar" ? item.labelAr : item.labelEn);

// "Common questions" as a chat: topics -> questions -> answer -> "did this answer your question?".
// Anonymous by design: nothing is asked and no conversation exists until the visitor chooses to
// contact support.
export default function FaqView({ c, lang }) {
  const { g, faq, faqNav, faqDetails } = c;
  const topic = faq.categories.find((item) => item.value === faqNav.category) || null;
  const question = faqNav.key ? faq.items.find((item) => item.key === faqNav.key) : null;
  const detail = faqNav.key ? faqDetails[faqNav.key] : null;

  const contact = (
    <Quick icon={<LifeBuoy size={15} aria-hidden="true" />} onClick={() => c.startIntake()}>{g.contactSupport}</Quick>
  );

  let body;
  let footer;

  if (faq.status === "loading" || faq.status === "idle") {
    body = (
      <div className="lc-skeletons" role="status" aria-label={g.faqLoading}>
        {[0, 1, 2].map((i) => <div className="lc-skeleton short" key={i} />)}
      </div>
    );
  } else if (faq.status === "error") {
    body = (
      <>
        <Assistant g={g} tone="error">{faq.error || g.faqLoadError}</Assistant>
        <QuickReplies>
          <Quick icon={<RefreshCw size={15} aria-hidden="true" />} onClick={() => c.loadFaqs({ force: true })}>{c.t.retry}</Quick>
          {contact}
        </QuickReplies>
      </>
    );
  } else if (!faq.items.length) {
    body = (
      <>
        <Assistant g={g}>{g.faqEmpty}</Assistant>
        <QuickReplies>{contact}</QuickReplies>
      </>
    );
  } else if (faqNav.key) {
    const questionText = question ? question.question[lang] : detail?.data?.question?.[lang] || "";
    body = (
      <>
        <UserBubble g={g}>{questionText}</UserBubble>
        {(!detail || detail.status === "loading") && <Assistant g={g}><span className="lc-muted-line">{c.t.loading}</span></Assistant>}
        {detail?.status === "error" && (
          <>
            <Assistant g={g} tone="error">{detail.error || g.faqAnswerError}</Assistant>
            <QuickReplies>
              <Quick icon={<RefreshCw size={15} aria-hidden="true" />} onClick={() => c.loadFaqDetail(faqNav.key)}>{c.t.retry}</Quick>
            </QuickReplies>
          </>
        )}
        {detail?.status === "ready" && (
          <>
            <Assistant g={g}><p className="lc-answer">{detail.data.answer[lang]}</p></Assistant>
            {faqNav.outcome === "helpful" ? (
              <>
                <Assistant g={g}>{g.helpfulClosing}</Assistant>
                <QuickReplies>
                  <Quick onClick={c.showHome}>{g.backToStart}</Quick>
                </QuickReplies>
              </>
            ) : (
              <>
                <Assistant g={g}>{g.didItHelp}</Assistant>
                <QuickReplies label={g.didItHelp}>
                  <Quick primary onClick={() => c.faqHelpful(faqNav.key)}>{g.yesHelped}</Quick>
                  <Quick primary onClick={() => c.faqNotHelpful(faqNav.key)}>{g.noNeedHelp}</Quick>
                </QuickReplies>
              </>
            )}
          </>
        )}
      </>
    );
    footer = (
      <div className="lc-flow-foot">
        <Button variant="neutral" appearance="text" size="sm" onClick={c.faqBack}>{g.anotherQuestion}</Button>
        <Button variant="neutral" appearance="text" size="sm" onClick={() => c.startIntake()}>{g.contactSupport}</Button>
      </div>
    );
  } else if (topic) {
    const items = faq.items.filter((item) => item.category === topic.value);
    body = (
      <>
        <Assistant g={g}>{g.faqQuestionsPrompt(labelOf(topic, lang))}</Assistant>
        <QuickReplies label={labelOf(topic, lang)}>
          {items.map((item) => (
            <Quick key={item.key} onClick={() => c.faqOpenQuestion(item.key)}>{item.question[lang]}</Quick>
          ))}
        </QuickReplies>
        <QuickReplies>
          <Quick onClick={c.faqRestart}>{g.otherTopics}</Quick>
          {contact}
        </QuickReplies>
      </>
    );
  } else {
    body = (
      <>
        <Assistant g={g}>{g.faqTopicsPrompt}</Assistant>
        <QuickReplies label={g.commonQuestions}>
          {faq.categories.map((item) => (
            <Quick key={item.value} onClick={() => c.faqOpenCategory(item.value)}>{labelOf(item, lang)}</Quick>
          ))}
        </QuickReplies>
        <QuickReplies>{contact}</QuickReplies>
      </>
    );
  }

  return (
    <Flow g={g} footer={footer} scrollKey={`${faq.status}:${faqNav.category}:${faqNav.key}:${faqNav.outcome}:${detail?.status}`}>
      {body}
    </Flow>
  );
}
